/**
 * --------------------------------------------------------------------------
 * CoreUI chip.ts
 * Licensed under MIT (https://github.com/coreui/coreui/blob/main/LICENSE)
 * --------------------------------------------------------------------------
 */

import BaseComponent from './base-component.js'
import EventHandler from './dom/event-handler.js'
import SelectorEngine from './dom/selector-engine.js'
import { sanitizeByConfig, type SanitizerAllowList, SVGAllowlist } from './util/sanitizer.js'
import { type ChipRemoveLabel, getChipRemoveLabel } from './util/chip-values.js'
import { CHECK_ICON, REMOVE_ICON } from './util/icons.js'
import { defineJQueryPlugin, jQueryDispatch, writeAttribute } from './util/index.js'

/**
 * Constants
 */

const NAME = 'chip'
const DATA_KEY = 'coreui.chip'
const EVENT_KEY = `.${DATA_KEY}`
const DATA_API_KEY = '.data-api'

const EVENT_REMOVE = `remove${EVENT_KEY}`
const EVENT_REMOVED = `removed${EVENT_KEY}`
const EVENT_SELECT = `select${EVENT_KEY}`
const EVENT_SELECTED = `selected${EVENT_KEY}`
const EVENT_DESELECT = `deselect${EVENT_KEY}`
const EVENT_DESELECTED = `deselected${EVENT_KEY}`
const EVENT_CLICK = `click${EVENT_KEY}`
const EVENT_KEYDOWN = `keydown${EVENT_KEY}`

const SELECTOR_CHIP_CHECK = '.chip-check'
const SELECTOR_CHIP_REMOVE = '.chip-remove'
const SELECTOR_DATA_CHIP = '[data-coreui-chip]'

const CLASS_NAME_CHIP_CHECK = 'chip-check'
const CLASS_NAME_CHIP_CLICKABLE = 'chip-clickable'
const CLASS_NAME_CHIP_REMOVE = 'chip-remove'
const CLASS_NAME_ACTIVE = 'active'
const CLASS_NAME_DISABLED = 'disabled'

type ChipConfig = {
  allowList: SanitizerAllowList
  ariaRemoveLabel: ChipRemoveLabel
  disabled: boolean
  filter: boolean
  removable: boolean
  removeIcon: string
  sanitize: boolean
  sanitizeFn: ((unsafeHtml: string) => string) | null
  selectable: boolean
  selected: boolean
  selectedIcon: string
}

const Default: ChipConfig = {
  allowList: SVGAllowlist,
  ariaRemoveLabel: 'Remove',
  disabled: false,
  filter: false,
  removable: false,
  removeIcon: REMOVE_ICON,
  sanitize: true,
  sanitizeFn: null,
  selectable: false,
  selected: false,
  selectedIcon: CHECK_ICON
}

const DefaultType = {
  allowList: 'object',
  ariaRemoveLabel: '(string|function)',
  disabled: 'boolean',
  filter: 'boolean',
  removable: 'boolean',
  removeIcon: 'string',
  sanitize: 'boolean',
  sanitizeFn: '(null|function)',
  selectable: 'boolean',
  selected: 'boolean',
  selectedIcon: 'string'
}

/**
 * Class definition
 */

class Chip extends BaseComponent {
  protected declare _checkIcon: HTMLElement | null
  protected declare _disabled: any
  protected declare _removeControl: HTMLElement | null
  protected declare _selected: any

  constructor(element?: string | Element | null, config?: Partial<ChipConfig> | null) {
    super(element, config)

    this._checkIcon = null
    this._disabled = this._config.disabled
    this._removeControl = null
    this._selected = this._config.selected

    this._applyRole()
    this._ensureRemoveControl()
    this._applyState()
    this._makeFocusable()

    this._addEventListeners()
  }

  // Getters
  static override get Default(): typeof Default {
    return Default
  }

  static override get DefaultType(): typeof DefaultType {
    return DefaultType
  }

  static override get NAME(): string {
    return NAME
  }

  // Public
  remove(): void {
    const removeEvent = EventHandler.trigger(this._element, EVENT_REMOVE)

    if (removeEvent.defaultPrevented) {
      return
    }

    this._destroyElement()
  }

  toggle(): void {
    if (!this._config.selectable) {
      return
    }

    if (this._selected) {
      this.deselect()
      return
    }

    this.select()
  }

  select(): void {
    if (!this._config.selectable) {
      return
    }

    if (this._selected) {
      return
    }

    const selectEvent = EventHandler.trigger(this._element, EVENT_SELECT)
    if (selectEvent.defaultPrevented) {
      return
    }

    this._selected = true
    this._applyState()

    EventHandler.trigger(this._element, EVENT_SELECTED)
  }

  deselect(): any {
    if (!this._config.selectable) {
      return
    }

    if (!this._selected) {
      return
    }

    const deselectEvent = EventHandler.trigger(this._element, EVENT_DESELECT)
    if (deselectEvent.defaultPrevented) {
      return
    }

    this._selected = false
    this._applyState()

    EventHandler.trigger(this._element, EVENT_DESELECTED)
  }

  override dispose(): void {
    if (!this._element) {
      return
    }

    this._checkIcon?.remove()
    this._removeControl?.remove()

    super.dispose()
  }

  // Private
  _configAfterMerge(config: any): any {
    // A filter chip is selectable by definition.
    if (config.filter) {
      config.selectable = true
    }

    return config
  }

  _addEventListeners(): void {
    EventHandler.on(this._element, EVENT_KEYDOWN, event => this._handleKeydown(event))

    EventHandler.on(this._element, EVENT_CLICK, event => {
      if (this._disabled) {
        return
      }

      if ((event.target as HTMLElement).closest(SELECTOR_CHIP_REMOVE)) {
        return
      }

      if (event.shiftKey && this._element.getAttribute('role') === 'option') {
        return
      }

      this.toggle()
    })

    EventHandler.on(this._element, EVENT_CLICK, SELECTOR_CHIP_REMOVE, event => {
      event.stopPropagation()

      if (this._config.removable && !this._disabled) {
        this.remove()
      }
    })
  }

  // A selectable chip needs a role its selection state is valid on: inside a
  // selectable chip set the set stamps role="option" (listbox pattern) before
  // initializing the chip; a standalone selectable chip is a toggle button.
  _applyRole(): void {
    const option = this._element.getAttribute('role') === 'option'

    writeAttribute(this._element, 'role', option ? 'option' : (this._config.selectable ? 'button' : null))
  }

  _applyState(): void {
    this._element.classList.toggle(CLASS_NAME_CHIP_CLICKABLE, !this._disabled && (this._config.clickable || this._config.selectable))

    this._element.classList.toggle(CLASS_NAME_DISABLED, this._disabled)

    // aria-disabled is not allowed on a generic element — only stamp it when
    // the chip carries a role; a role-less chip conveys the state through the
    // disabled class and the removed interactivity.
    if (this._disabled && this._element.hasAttribute('role')) {
      this._element.setAttribute('aria-disabled', 'true')
    } else {
      this._element.removeAttribute('aria-disabled')
    }

    const selected = this._config.selectable && this._selected
    const option = this._element.getAttribute('role') === 'option'

    this._element.classList.toggle(CLASS_NAME_ACTIVE, selected)
    writeAttribute(this._element, 'aria-selected', option ? String(selected) : null)
    writeAttribute(this._element, 'aria-pressed', !option && this._config.selectable ? String(selected) : null)

    if (this._config.filter) {
      if (selected) {
        this._ensureCheckIcon()
      } else {
        SelectorEngine.findOne(SELECTOR_CHIP_CHECK, this._element)?.remove()
      }
    }
  }

  _ensureCheckIcon(): any {
    if (SelectorEngine.findOne(SELECTOR_CHIP_CHECK, this._element)) {
      return
    }

    const check = document.createElement('span')
    check.className = CLASS_NAME_CHIP_CHECK
    check.setAttribute('aria-hidden', 'true')
    check.innerHTML = sanitizeByConfig(this._config.selectedIcon, this._config)
    this._element.prepend(check)
    this._checkIcon = check
  }

  _createRemoveControl(): HTMLElement {
    if (this._element.hasAttribute('role')) {
      const indicator = document.createElement('span')
      indicator.className = CLASS_NAME_CHIP_REMOVE
      indicator.setAttribute('aria-hidden', 'true')
      indicator.innerHTML = sanitizeByConfig(this._config.removeIcon, this._config)
      return indicator
    }

    const button = document.createElement('button')
    button.type = 'button'
    button.className = CLASS_NAME_CHIP_REMOVE
    button.setAttribute('aria-label', this._getRemoveLabel())
    button.setAttribute('tabindex', '-1')
    button.innerHTML = sanitizeByConfig(this._config.removeIcon, this._config)
    return button
  }

  _getRemoveLabel(): string {
    return getChipRemoveLabel(this._config.ariaRemoveLabel, this._element.textContent.replaceAll(/\s+/g, ' ').trim())
  }

  _ensureRemoveControl(): void {
    // A disabled chip is not interactive, so it never shows a remove button.
    if (!this._config.removable || this._disabled) {
      return
    }

    const existing = SelectorEngine.findOne(SELECTOR_CHIP_REMOVE, this._element)
    if (existing) {
      if (existing.tagName === 'BUTTON' && !existing.hasAttribute('aria-label')) {
        existing.setAttribute('aria-label', this._getRemoveLabel())
      }

      return
    }

    this._removeControl = this._createRemoveControl()
    this._element.append(this._removeControl)
  }

  _makeFocusable(): void {
    const focusable = !this._disabled && (this._config.selectable || this._config.removable)
    writeAttribute(this._element, 'tabindex', focusable ? '0' : null)
    writeAttribute(this._element, 'data-coreui-chip-focusable', focusable ? 'true' : null)
  }

  _handleKeydown(event: any): void {
    const { key } = event
    if (this._disabled) {
      return
    }

    switch (key) {
      case 'Enter':
      case ' ':
      case 'Spacebar': {
        if (!this._config.selectable) {
          return
        }

        event.preventDefault()
        this.toggle()
        break
      }

      case 'Backspace':
      case 'Delete': {
        if (this._config.removable) {
          event.preventDefault()
          this.remove()
        }

        break
      }

      // No default
    }
  }

  _destroyElement(): void {
    EventHandler.trigger(this._element, EVENT_REMOVED)
    this._element.remove()
    this.dispose()
  }

  // Static
  static chipInterface(element: string | Element | null, config?: any, ...args: any[]): void {
    const data: any = Chip.getOrCreateInstance(element, config)

    if (typeof config === 'string') {
      if (typeof data[config as string] === 'undefined') {
        throw new TypeError(`No method named "${config}"`)
      }

      data[config as string](...args)
    }
  }

  static jQueryInterface(this: any, config: any): void {
    return jQueryDispatch(this, Chip, config, element => [element])
  }
}

/**
 * Data API implementation
 */

EventHandler.on(document, `DOMContentLoaded${EVENT_KEY}${DATA_API_KEY}`, () => {
  for (const element of SelectorEngine.find(SELECTOR_DATA_CHIP)) {
    Chip.chipInterface(element)
  }
})

/**
 * jQuery
 */

defineJQueryPlugin(Chip)

export default Chip
export type { ChipConfig }
